import React, {useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import {useRouter} from 'next/router';
import {useKeyPress} from 'react-use';
import Box from '@mui/material/Box';
import classNames from 'classnames';
import ButtonBack from '../../components/ButtonBack/ButtonBack';
import ButtonNext from '../../components/ButtonNext/ButtonNext';
import Counter from '../../components/Counter/Counter';
import Feature from '../../components/FeatureFlag/Flags';
import Loader from '../../components/Loader/Loader';
import NewProgressBar from '../../components/NewProgressBar/ProgressBar';
import SuccessSnackbar from '../../components/Snackbar';
import {useStore} from '../../components/StoreProvider';
import useFeatureFlag from '../../components/FeatureFlag/useFeatureFlag';
import useHubSpotChatLogging from '../../helpers/useHubSpotChatLogging';
import {useIsOverflow} from '../../helpers/useIsOverflow';
import useScrollDirection from '../../helpers/useScrollDirection';
import useWindowSize from '../../helpers/useWindowResize';
import Header from '../../slidesParts/Header/Header';
import {toNextPhase} from '../../slidesParts/NewAmplifyCalculator/helpers';
import ScreenSwiper from '../../slidesParts/ScreenSwiper/ScreenSwiper';
import SlideContainer, {
    BackActionReturn,
    NextActionReturn,
    SlideContainerProps,
} from '../../slidesParts/SlideContainer/SlideContainer';
import {HEALTH_ROUTE} from '../../store/structures/phaseList';

import {serialize} from '../../store/sync/serialization';
import {otherValue} from '../../store/UserData/types';
import styles from './SlidesWithProgressBar.module.scss';

type SlidesWithProgressBarProps = {
    noTopAnimation?: boolean;
    children?: React.ReactNode;
    waitOnAutoProceed?: boolean;
    route: string;
    isHideAmplifyLogo?: boolean;
};

const SlidesWithProgressBar: React.FC<SlidesWithProgressBarProps> = (props) => {
    const router = useRouter();
    const [isRouterReady, setIsRouterReady] = useState(false);
    const store = useStore();
    const {children, noTopAnimation} = props;
    const slides = useRef(null);
    const [isNextSlideTimeoutDone, setIsNextSlideTimeoutDone] = useState(false);
    const {width, height} = useWindowSize();

    const sliderContainerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const {isOverflow: isOverflowGeneral} = useIsOverflow();
    const {isOverflow, calculateOverflow} = useIsOverflow(sliderContainerRef);

    const scrollDirection = useScrollDirection(sliderContainerRef, headerRef);
    const {uiStore, logStore, calcStore} = store;
    const {progressStore, componentsStore} = uiStore;

    // Hotjar and Microsoft Clarity both record these screens. Each honours a DOM attribute
    // that masks the text inside it, so the phase-2 answers never reach either recording.
    // Applied to the whole phase rather than individual inputs: the questions are as
    // revealing as the answers, and a new slide would otherwise be captured by default.
    const isHealthPhase = props.route === HEALTH_ROUTE;
    const recordingMask = isHealthPhase ? {'data-hj-suppress': true, 'data-clarity-mask': 'true'} : {};

    const getChildrenProps = (): SlideContainerProps[] => {
        return (React.Children.toArray(children) as {props: SlideContainerProps}[]).map((c) => c.props);
    };

    const [slidesCount, setSlidesCount] = useState<number>(0);
    const [slidesProps, setSlidesProps] = useState<SlideContainerProps[]>(getChildrenProps);
    const [isAutoNextDisabled, setIsAutoNextDisabled] = useState<Record<string, boolean | string>>({
        slideName: '',
        disabled: false,
    });
    const [isSliding, setIsSliding] = useState<boolean>(false);

    const slideProps = slidesProps[progressStore.slide];
    const isConsentDrawerActive = !!componentsStore.consentActiveDrawer && slideProps?.withDrawer;
    const intentBuilder3266 = true;
    // useFeatureFlag(Feature.intentBuilder3266);
    const howDidYouHear3406 = useFeatureFlag(Feature.howDidYouHear3406);
    const stickyCTA4274 = useFeatureFlag(Feature.stickyCTA4274);

    useHubSpotChatLogging(slidesProps[progressStore.slide]?.slideName);

    const searchForChat = () => {
        const findChatAndApplyStyles = () => {
            const chat = document?.getElementById('hubspot-messages-iframe-container');
            const isDrawerActive =
                !!componentsStore.consentActiveDrawer && slidesProps[progressStore.slide]?.withDrawer;

            if (chat) {
                // tricky way to hide chat, attached to ref of container
                const hidechat = sliderContainerRef?.current?.dataset?.hidechat === 'true';
                if (hidechat) {
                    chat.style.opacity = '0';
                    sliderContainerRef.current.dataset.hidechat = 'false';
                    setTimeout(() => (chat.style.opacity = '1'), 1000);
                }

                chat.classList.add(styles.chat);

                if (isOverflow) {
                    chat.classList.add(styles.chat_overflowed);
                } else {
                    chat.classList.remove(styles.chat_overflowed);
                }

                // quotes page, calculator/req slide chat logic
                if (progressStore.route === 'quotes' && progressStore.slide > 0) {
                    chat.classList.add(styles.chat_quotesPage);
                } else {
                    chat.classList.remove(styles.chat_quotesPage);
                }

                if (isDrawerActive) {
                    chat.classList.add(styles.activeDrawer);
                } else {
                    chat.classList.remove(styles.activeDrawer);
                }
            }
        };

        findChatAndApplyStyles();
        const observer = new MutationObserver(() => {
            findChatAndApplyStyles();
        });
        observer.observe(document, {
            childList: true,
            subtree: true,
        });
    };

    const getIsOptionOtherSelected = !!store.userData.phase2.referralSources.find(
        (option: {label: string; value: boolean | string}) =>
            option.label === otherValue.Label && (typeof option.value !== 'boolean' ? true : option?.value),
    );
    useEffect(() => {
        searchForChat();
    }, []);

    useEffect(() => {
        searchForChat();
    }, [progressStore.slide, width, height, isOverflowGeneral]);

    useEffect(() => {
        setIsAutoNextDisabled({
            slideName: slidesProps[progressStore.slide]?.slideName,
            disabled: !!slidesProps[progressStore.slide]?.disableAutoSlide,
        });
    }, [progressStore?.slide, slidesProps]);

    useEffect(() => {
        if (slideProps?.forceAutoSlide) {
            setIsSliding(true);
        }
    }, [slideProps?.forceAutoSlide]);

    const updPercent = () => {
        progressStore.setProgressBar({
            percent:
                progressStore.slide === 0 && router.pathname === '/basic'
                    ? 0.035
                    : (progressStore.slide + 1) / slidesCount,
        });
    };

    useEffect(updPercent, [slidesCount, progressStore.slide]);

    useEffect(() => {
        setSlidesCount(getChildrenProps().length);
        setSlidesProps(getChildrenProps());
        if (slidesCount) updPercent();
    }, [props]);

    useEffect(() => {
        if (isSliding) {
            sliding();
        }
    }, [isSliding]);

    useEffect(() => {
        if (isNextSlideTimeoutDone && !props.waitOnAutoProceed) {
            // move past first slide when both timeout is finished and loading is finished
            clickNext();
            setIsSliding(true);
            setIsNextSlideTimeoutDone(false);
        }
    }, [props.waitOnAutoProceed, isNextSlideTimeoutDone]);

    const scrollToTop = () => {
        if (sliderContainerRef?.current && window.scrollY > 0) {
            sliderContainerRef.current.scrollIntoView({behavior: 'auto'});
        }
    };

    // Check if selectedProduct is available
    const selectedProduct = calcStore.getActiveProductCode();
    const isProductSelected = !!selectedProduct;

    const nextDisabled = (): boolean => {
        if (isLoadingSlide && getIsOptionOtherSelected) {
            if (typeof slidesProps[progressStore.slide]?.validation === 'function') {
                return !slidesProps[progressStore.slide]?.validation();
            }
            return false;
        }
        if (
            isSliding ||
            progressStore.loading ||
            progressStore.resettingQuote ||
            (isLoadingSlide &&
                (!isProductSelected || slidesProps[progressStore.slide]?.slideName !== 'referral-source'))
        )
            return true;
        if (typeof slidesProps[progressStore.slide]?.validation === 'function') {
            return !slidesProps[progressStore.slide].validation();
        }
        return false;
    };

    const sliding = async () => {
        let nextResult: NextActionReturn[] = [true];
        if (slideProps?.nextAction || slideProps?.nextActionAsync) {
            nextResult = [];
            if (slideProps.nextAction) nextResult.push(slideProps.nextAction());
            if (slideProps.nextActionAsync) nextResult.push(await slideProps.nextActionAsync());
        }
        if (nextResult.every((r) => !!r)) {
            if (nextResult.some((r) => r === 'nextPhase')) {
                progressStore.toNextPhase();
                return;
            }

            progressStore.slide < slidesCount - 1 ? progressStore.toNextSlide() : progressStore.toNextPhase();
            scrollToTop();
        }
        setIsSliding(false);
    };

    const clickNext = async () => {
        if (nextDisabled()) return;
        setIsSliding(true);
    };

    const moveToNext = () => {
        if (
            !isAutoNextDisabled?.disabled &&
            isAutoNextDisabled?.slideName === slidesProps[progressStore.slide]?.slideName
        ) {
            clickNext();
        }
    };

    const clickBack = async () => {
        let backResult: BackActionReturn[] = [true];

        if (slideProps?.backAction || slideProps?.backActionAsync) {
            backResult = [];
            if (slideProps.backAction) backResult.push(slideProps.backAction());
            if (slideProps.backActionAsync) backResult.push(await slideProps.backActionAsync());
        }

        if (backResult.every((r) => !!r)) {
            if (backResult.some((r) => r === 'previousPhase')) {
                progressStore.toPreviousPhase();
                return;
            }
            progressStore.slide > 0 ? progressStore.toPreviousSlide() : progressStore.toPreviousPhase();
            scrollToTop();
        }
    };

    useEffect(() => {
        if (router?.isReady) setIsRouterReady(true);
    }, [router?.isReady]);

    useEffect(() => {
        calculateOverflow();
    }, [progressStore.slide]);

    const onView = () => {
        const currentSlideProps = slidesProps?.[progressStore?.slide];
        const {onView: onViewSlide, slideName, analyticsProps, isConditionalSlide} = currentSlideProps || {};
        onViewSlide && onViewSlide();
        const currentRoute = (progressStore.route || 'noName').split('?')[0].split('-')[0];
        const action = `${currentRoute}-${slideName || 'noName'}`;
        store.userData.updateField('recommendedProductTypeMx', calcStore.recommendedProductType);

        // Conditional slides emit no analytics at all. They render only because of a previous
        // answer — `asthma` means they ticked asthma, `behavior-dui` means they ticked DUI — so
        // the event's mere existence discloses it. A generic label does not help: the slides
        // render in a fixed filtered order, so position and count still reconstruct the answers,
        // and a single conditional after a yes/no gate is a complete disclosure on its own.
        //
        // currentSlideName is skipped for the same reason: it is only read back as `eventOrigin`
        // on the Mixpanel payload, so leaving it on the last unconditional slide keeps the
        // disclosure out of the following event too.
        //
        // Per-condition drop-off is still available in aggregate from the session records
        // Athena already holds, without exporting anyone's event stream to a third party.
        if (!isConditionalSlide) {
            progressStore.setCurrentSlideName(action);
            // TODO maybe not needed since we don't show children before utm is set
            setTimeout(() => {
                logStore.logAction({
                    action: analyticsProps?.action || action,
                    data: {
                        ...serialize(store.userData),
                        ...(action === 'quotes-amplify-calculator'
                            ? {
                                  initialProductRecommendation: {
                                      recommendedProductType: calcStore.recommendedProductTypeEnum || '',
                                      resultBest: store.userData.phase3.resultBest,
                                      resultRetirement: store.userData.phase3.resultRetirement,
                                      resultTemporary: store.userData.phase3.resultTemporary,
                                      resultVul: store.userData.phase3.vul,
                                  },
                                  userWasRecommendedProsperProduct:
                                      store.userData.useProsperFlow &&
                                      calcStore.products.some(
                                          (product) => product.code === 'PROSPERIUL' || product.code === 'LSWIUL',
                                      )
                                          ? 'Yes'
                                          : 'No',
                              }
                            : {}),
                        ...analyticsProps?.data,
                    },
                });
            });
        }

        if (slideProps?.autoOpenKeyboard) {
            const focusableDom = document.querySelector(
                `input[name=${slideProps?.autoOpenKeyboard}]`,
            ) as HTMLInputElement;
            if (focusableDom) {
                setTimeout(() => focusableDom?.focus(), 300);
            }
        }
    };

    const showNavButtons = !!slidesCount && slidesProps[progressStore.slide]?.hideNavigation !== true;

    const isRecommendedPage = progressStore.slide === 1 && progressStore.phase === 3;
    const isLoadingSlide = progressStore.slide === 0 && progressStore.phase === 3;

    const navButtons = (
        <>
            <div id="navButtons" className={styles.bottomButtons_offset} />
            <div
                data-testid={'navButtons'}
                className={classNames(styles.bottomButtons, {
                    [styles.bottomButtons_isOverflow]: isOverflow && !stickyCTA4274,
                    [styles.bottomButtons_stickyMode]: stickyCTA4274,
                    [styles.bottomButtons_withDrawer]: isConsentDrawerActive,
                })}
            >
                <div
                    className={classNames(styles.bottomButtons_container, {
                        [styles.bottomButtons_containerFirstSlide]: intentBuilder3266,
                    })}
                >
                    <ButtonBack
                        onClick={clickBack}
                        disabled={false}
                        className={classNames(styles.bottomButtons_prev, {
                            [styles.bottomButtons_hidden]: progressStore.slide === 0 && progressStore.phase === 0,
                        })}
                    />
                    {isLoadingSlide && !getIsOptionOtherSelected ? (
                        <ButtonNext
                            ariaLabel={`slide-${progressStore.slide}`}
                            className={styles.bottomButtons_next_animated}
                            disabled={nextDisabled()}
                            onClick={clickNext}
                        >
                            <div style={{display: 'flex'}}>
                                crunching numbers
                                <div className={styles.dot} />
                            </div>
                        </ButtonNext>
                    ) : (
                        <ButtonNext
                            className={classNames(styles.bottomButtons_next, styles[slideProps?.className])}
                            ariaLabel={`slide-${progressStore.slide}`}
                            data-testid={'NextButton'}
                            onClick={
                                isRecommendedPage &&
                                calcStore.activeProduct !== 'annuity' &&
                                calcStore.activeProduct !== 'fexp' &&
                                calcStore.activeProduct !== 'adb'
                                    ? () => {
                                          toNextPhase(store, selectedProduct);
                                      }
                                    : clickNext
                            }
                            disabled={nextDisabled()}
                            textBtn={slideProps?.customNextButtonText || 'Next'}
                            debounceTime={400}
                        />
                    )}
                    {isLoadingSlide && (
                        <Counter
                            phase={progressStore?.phase}
                            onClearTimeOut={
                                progressStore.slide === 0 &&
                                (() => {
                                    if (!getIsOptionOtherSelected) {
                                        setIsNextSlideTimeoutDone(true);
                                    }
                                })
                            }
                            className={styles.bottomButtons_counter}
                            duration={howDidYouHear3406 ? 7 : 5}
                        />
                    )}
                    {slideProps?.additionalButton}
                </div>
            </div>
        </>
    );

    const checkPathInput = (path: HTMLElement[], target: HTMLElement): boolean =>
        path?.[0]?.localName === 'input' || target?.localName === 'input';

    const [nextPressed] = useKeyPress((e) => {
        // Android mobiles only gave the e.key as Enter and not the code
        if (e.key === 'Enter' || e.code === 'Enter') return true;
        if (checkPathInput((e as unknown as {path?: []}).path, e?.target as HTMLElement)) return false;
        return ['Enter', 'ArrowRight'].includes(e.code);
    });

    const [backPressed] = useKeyPress((e) => {
        if (checkPathInput((e as unknown as {path?: []}).path, e?.target as HTMLElement)) return false;
        return ['ArrowLeft'].includes(e.code);
    });

    useEffect(() => {
        if (nextPressed) {
            clickNext();
        }
    }, [nextPressed]);

    useEffect(() => {
        if (backPressed) {
            clickBack();
        }
    }, [backPressed]);

    const newProgressBar = !slidesProps[progressStore.slide]?.hideProgressBar && (
        <NewProgressBar className={isConsentDrawerActive && styles.progressBarWithDrawer} />
    );

    if (!isRouterReady) return <></>;

    return (
        <>
            {slidesProps[progressStore.slide]?.showSnackBar && (
                <SuccessSnackbar title="Amazing!" subtitle="Thank you for your service!" />
            )}
            <Header
                ref={headerRef}
                scrollDirection={scrollDirection}
                className={classNames({
                    [styles.header_withDrawer]: isConsentDrawerActive,
                })}
                isHideAmplifyLogo={props?.isHideAmplifyLogo}
            />
            <Box
                ref={sliderContainerRef}
                id="slider-container"
                data-testid={'SlideContainer'}
                data-hidechat="true"
                {...recordingMask}
                className={classNames(styles.layout, {
                    [styles.topOffset]: Math.round(scrollDirection?.y) === 0,
                })}
                sx={{
                    // The nav bar is pinned above the consent prompt and #navButtons reserves the
                    // matching space in flow, so no banner offset is needed here.
                    height: progressStore.phase === 3 ? `calc(100vh - ${width < 1200 ? 149 : 84}px)` : '100%',
                }}
            >
                {newProgressBar}
                <div
                    className={classNames(styles.slides, {
                        [styles?.withDrawer]: isConsentDrawerActive,
                    })}
                    ref={slides}
                >
                    {progressStore.loading || progressStore.resettingQuote ? (
                        <SlideContainer slideName="loader">
                            <div style={{height: 80}} />
                            <Loader />
                        </SlideContainer>
                    ) : (
                        <ScreenSwiper
                            noTopAnimation={noTopAnimation}
                            slideNumber={progressStore.slide}
                            notDetectHeight={slideProps?.disableAutoHeight}
                            onTransitionEnd={onView}
                            automaticNextSlide={moveToNext}
                            disableOverflow={progressStore.route === 'quotes'}
                        >
                            {children}
                        </ScreenSwiper>
                    )}
                </div>
                {showNavButtons && (!progressStore.loading || !progressStore.resettingQuote) && navButtons}
            </Box>
        </>
    );
};

export default observer(SlidesWithProgressBar);
